import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Truck, Trash2, Hash, X, ExternalLink, Mail, RefreshCw, FileText, DollarSign, CreditCard, ChevronDown, Package, Download, MapPin, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function OrderDetailDialog({
  open,
  onOpenChange,
  order,
  orderSerials,
  productTrackInfo,
  trackingNumber,
  setTrackingNumber,
  onUpdateOrder,
  onDeallocateSerial,
  onShip,
  onProcess,
  onDelete,
  onSyncXero,
  onResendConfirmation,
  onSendInvoice,
  onMarkPaid,
  getStatusColor,
}) {
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingQuotes, setBookingQuotes] = useState([]);
  const [selectedCourier, setSelectedCourier] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [labelDownloading, setLabelDownloading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (open) {
      axios.get(`${API}/api/payment-methods/active`)
        .then(res => setPaymentMethods(res.data.methods || []))
        .catch(() => setPaymentMethods([
          { id: 'eftpos', name: 'EFTPOS' },
          { id: 'cash', name: 'Cash' },
          { id: 'direct_deposit', name: 'Direct Deposit' },
        ]));
    }
  }, [open]);
  
  if (!order) return null;

  const trackedItems = (order.items || []).filter(item => productTrackInfo[item.product_id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">
            Order #{order.order_number || order.id.slice(0, 8).toUpperCase()}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 mt-4">
          {/* Customer Info */}
          <div>
            <h3 className="text-sm font-medium text-zinc-400 mb-2">Customer</h3>
            <p className="text-white">{order.user?.first_name} {order.user?.last_name}</p>
            <p className="text-zinc-500">{order.user?.email}</p>
          </div>

          {/* Order Items */}
          <div>
            <h3 className="text-sm font-medium text-zinc-400 mb-2">Items</h3>
            <div className="space-y-2">
              {order.items?.map((item, idx) => {
                const itemSerials = orderSerials.filter(s => s.product_id === item.product_id);
                return (
                  <div key={idx} className="bg-zinc-800 rounded overflow-hidden">
                    <div className="flex justify-between text-sm p-3">
                      <span className="text-white">{item.product_name} x {item.quantity}</span>
                      <div className="flex items-center gap-3">
                        {itemSerials.length > 0 && (
                          <span className="text-xs text-[#78BE20] flex items-center gap-1">
                            <Hash className="h-3 w-3" /> {itemSerials.length}/{item.quantity}
                          </span>
                        )}
                        <span className="text-[#78BE20] font-mono">${item.total_price.toFixed(2)}</span>
                      </div>
                    </div>
                    {itemSerials.length > 0 && (
                      <div className="border-t border-zinc-700 px-3 py-2">
                        <div className="flex flex-wrap gap-1.5">
                          {itemSerials.map((s, si) => (
                            <span key={si} className="inline-flex items-center gap-1 bg-zinc-900 text-xs font-mono px-2 py-1 rounded text-white">
                              {s.serial_number}
                              <button onClick={() => onDeallocateSerial(s.serial_number)} className="text-zinc-500 hover:text-red-400 ml-1" data-testid={`remove-serial-${si}`}>
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Allocated Serials Summary */}
          {trackedItems.length > 0 && orderSerials.length > 0 && (
            <div className="border border-zinc-800 rounded-lg p-3 bg-zinc-800/30">
              <div className="flex items-center gap-2 mb-2">
                <Hash className="h-4 w-4 text-[#78BE20]" />
                <h3 className="text-sm font-medium text-white">Allocated Serials</h3>
                <span className="text-xs text-zinc-500">({orderSerials.length} total)</span>
              </div>
              {trackedItems.map((item, idx) => {
                const itemSerials = orderSerials.filter(s => s.product_id === item.product_id);
                if (itemSerials.length === 0) return null;
                return (
                  <div key={idx} className="mb-2 last:mb-0">
                    <p className="text-xs text-zinc-500 mb-1">{item.product_name}:</p>
                    <div className="flex flex-wrap gap-1">
                      {itemSerials.map((s, si) => (
                        <span key={si} className="text-xs bg-zinc-900 text-white font-mono px-2 py-0.5 rounded">{s.serial_number}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Shipping Address */}
          <div>
            <h3 className="text-sm font-medium text-zinc-400 mb-2">Shipping Address</h3>
            <p className="text-zinc-300 text-sm">
              {order.shipping_address?.street}<br />
              {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.postcode}
            </p>
          </div>

          {/* Customer Notes */}
          {order.customer_notes && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-sm p-3" data-testid="customer-notes">
              <h3 className="text-sm font-medium text-amber-400 mb-1">Customer Notes</h3>
              <p className="text-zinc-300 text-sm">{order.customer_notes}</p>
            </div>
          )}

          {/* Update Status */}
          <div>
            <Label>Order Status</Label>
            <div className="flex gap-2 mt-1">
              <Select
                value={order.status}
                onValueChange={(v) => {
                  if (v === 'shipped') {
                    onShip();
                  } else if (v === 'processing') {
                    // disabled in select, handled by button
                  } else {
                    onUpdateOrder(order.id, { status: v });
                  }
                }}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 flex-1" data-testid="order-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing" disabled>Processing</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              {order.status === 'pending' && (
                <Button
                  className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                  onClick={() => onProcess(order)}
                  data-testid="process-order-btn"
                >
                  <Hash className="h-4 w-4 mr-1" /> Process Order
                </Button>
              )}
            </div>
          </div>

          {/* Tracking Number */}
          <div>
            <Label>Tracking Number</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Enter tracking number"
              />
              <Button
                onClick={() => onUpdateOrder(order.id, { tracking_number: trackingNumber })}
                className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
              >
                <Truck className="h-4 w-4 mr-2" />
                Update
              </Button>
            </div>
          </div>

          {/* Shipment Booking Section */}
          {order.shipping_courier && order.shipping_courier !== 'local_pickup' && order.shipping_courier !== 'flat_rate' && (
            <div className="border border-zinc-800 rounded-sm p-4 space-y-3" data-testid="shipment-booking-section">
              <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                <Package className="h-4 w-4" /> Shipment Booking
              </h3>

              {/* Show courier info */}
              <div className="flex items-center gap-3 text-sm">
                <span className="text-zinc-400">Courier:</span>
                <Badge className="bg-[#78BE20]/20 text-[#78BE20] capitalize">{(order.shipping_courier || '').replace(/_/g, ' ')}</Badge>
                {order.transdirect_booking_id && (
                  <span className="text-zinc-500">TD#{order.transdirect_booking_id}</span>
                )}
                {order.shipping_courier === 'auspost' && (
                  <Badge className="bg-red-500/20 text-red-400 text-[10px]">Australia Post</Badge>
                )}
              </div>

              {/* AusPost — manual label required */}
              {order.shipping_courier === 'auspost' ? (
                <div className="space-y-3">
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-start gap-3" data-testid="auspost-manual-label-notice">
                    <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-300">Manual Label Required</p>
                      <p className="text-xs text-amber-400/80 mt-1">
                        Australia Post labels must be created manually via your MyPost Business portal. 
                        Create the shipment there, then enter the tracking number below.
                      </p>
                    </div>
                  </div>
                  <a
                    href="https://auspost.com.au/mypost/business"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-[#78BE20] hover:underline"
                    data-testid="auspost-portal-link"
                  >
                    Open MyPost Business Portal <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              ) : order.shipping_booked_at ? (
                /* Transdirect: Already booked - show label/track buttons */
                <div className="space-y-2">
                  <Badge className="bg-green-500/20 text-green-400">Booked</Badge>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-zinc-700"
                      disabled={labelDownloading}
                      data-testid="download-label-btn"
                      onClick={async () => {
                        setLabelDownloading(true);
                        try {
                          const resp = await axios.get(
                            `${API}/api/admin/shipping/label/${order.transdirect_booking_id}`,
                            { ...authHeaders, responseType: 'blob' }
                          );
                          const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `label_${order.order_number}.pdf`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch {
                          toast.error('Label not available yet');
                        } finally {
                          setLabelDownloading(false);
                        }
                      }}
                    >
                      {labelDownloading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Download className="h-3 w-3 mr-1" />}
                      Download Label
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-zinc-700"
                      data-testid="track-shipment-btn"
                      onClick={async () => {
                        try {
                          const resp = await axios.get(`${API}/api/admin/shipping/track/${order.transdirect_booking_id}`, authHeaders);
                          setTrackingData(resp.data);
                          toast.success('Tracking data loaded');
                        } catch {
                          toast.error('Tracking not available');
                        }
                      }}
                    >
                      <MapPin className="h-3 w-3 mr-1" /> Track
                    </Button>
                  </div>
                  {trackingData && !trackingData.error && (
                    <div className="bg-zinc-800 rounded-sm p-2 text-xs text-zinc-300 mt-2">
                      <pre className="whitespace-pre-wrap">{JSON.stringify(trackingData, null, 2)}</pre>
                    </div>
                  )}
                </div>
              ) : (
                /* Transdirect: Not yet booked - show book button */
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-zinc-400">Pickup Date</Label>
                    <Input
                      type="date"
                      value={pickupDate}
                      onChange={(e) => setPickupDate(e.target.value)}
                      className="mt-1 bg-zinc-800 border-zinc-700 w-48"
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="pickup-date-input"
                    />
                  </div>
                  <Button
                    className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                    disabled={bookingLoading || !pickupDate}
                    data-testid="book-shipment-btn"
                    onClick={async () => {
                      if (!pickupDate) { toast.error('Please select a pickup date'); return; }
                      setBookingLoading(true);
                      try {
                        const resp = await axios.post(`${API}/api/admin/shipping/book`, {
                          order_id: order.id,
                          courier: order.shipping_courier,
                          pickup_date: pickupDate,
                          booking_id: order.transdirect_booking_id,
                        }, authHeaders);
                        if (resp.data.success) {
                          toast.success('Shipment booked! You can now download the label.');
                          onUpdateOrder(order.id, {});
                        } else {
                          toast.error(resp.data.error || 'Booking failed');
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to book shipment');
                      } finally {
                        setBookingLoading(false);
                      }
                    }}
                  >
                    {bookingLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Package className="h-4 w-4 mr-2" />}
                    Confirm Booking & Schedule Pickup
                  </Button>
                  <p className="text-zinc-500 text-xs">This will confirm the booking with {(order.shipping_courier || '').replace(/_/g, ' ')} and schedule a pickup from your warehouse.</p>
                </div>
              )}
            </div>
          )}

          {/* Totals */}
          <div className="border-t border-zinc-800 pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Subtotal</span>
              <span className="text-white font-mono">${order.subtotal?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Shipping ({order.shipping?.method})</span>
              <span className="text-white font-mono">${order.shipping?.cost?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold">
              <span className="text-white">Total</span>
              <span className="text-[#78BE20] font-mono">${order.total?.toFixed(2)}</span>
            </div>
            {order.payment_status === 'paid' && (
              <div className="flex justify-between text-sm pt-1">
                <span className="text-zinc-400">Payment</span>
                <Badge className="bg-[#78BE20]/20 text-[#78BE20]">
                  Paid{order.payment_method ? ` - ${order.payment_method}` : ''}
                </Badge>
              </div>
            )}
          </div>

          {/* Record Payment - for unpaid orders */}
          {onMarkPaid && order.payment_status !== 'paid' && (
            <div className="border-t border-zinc-800 pt-4">
              <div className="relative">
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => setShowPaymentOptions(!showPaymentOptions)}
                  data-testid="record-payment-btn"
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Record Payment
                  <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showPaymentOptions ? 'rotate-180' : ''}`} />
                </Button>
                {showPaymentOptions && (
                  <div className="mt-2 bg-zinc-800 border border-zinc-700 rounded-lg p-2 space-y-1" data-testid="payment-method-options">
                    <p className="text-xs text-zinc-500 px-2 py-1">Select payment method:</p>
                    {paymentMethods.map(method => (
                      <Button
                        key={method.id}
                        variant="ghost"
                        className="w-full justify-start text-zinc-300 hover:text-white hover:bg-zinc-700 h-9"
                        onClick={() => { onMarkPaid(order, method.name); setShowPaymentOptions(false); }}
                        data-testid={`pay-method-${method.id}`}
                      >
                        <CreditCard className="h-3.5 w-3.5 mr-2 text-green-400" />
                        {method.name}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Xero Sync, Resend & Delete */}
          <div className="border-t border-zinc-800 pt-4 space-y-2">
            {/* Send Invoice - for unpaid orders (cash/direct deposit) */}
            {onSendInvoice && order.payment_status !== 'paid' && (
              <Button
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => onSendInvoice(order)}
                data-testid="send-invoice-btn"
              >
                <FileText className="h-4 w-4 mr-2" />
                Send Invoice (for Payment)
              </Button>
            )}
            {onResendConfirmation && (
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => onResendConfirmation(order)}
                data-testid="resend-confirmation-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Resend Confirmation Email
              </Button>
            )}
            {!order.xero_invoice_id ? (
              <Button
                className="w-full bg-[#13B5EA] hover:bg-[#0fa0d0] text-white"
                onClick={() => onSyncXero(order)}
                data-testid="sync-xero-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Sync to Xero
              </Button>
            ) : (
              <div className="text-center text-xs text-zinc-500">
                Synced to Xero · Invoice ID: <span className="font-mono text-zinc-400">{order.xero_invoice_id.slice(0, 8)}...</span>
              </div>
            )}
            <Button
              variant="destructive"
              className="w-full bg-red-600 hover:bg-red-700"
              onClick={() => onDelete(order)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Force Delete Order
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
